import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  isElectron,
  useAuthenticateMagicAuth,
  useSendMagicAuthCode,
} from '@fluxter/shared';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { tokenStore } from '@/lib/api';
import { isAccessGateBlocked } from '@/lib/auth-errors';
import { consumeMagicAuthEmail, peekMagicAuthEmail } from '@/lib/sign-in';
import { consumePostLoginReturnUrl } from '@/lib/post-login-redirect';
import { AuthHeader, AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';

const CODE_LENGTH = 6;
const deviceName = isElectron() ? 'desktop' : 'web';

/**
 * Slice 1+2 own-UI: second step of the magic-auth flow.
 *
 * Reads the email stashed by `LoginPage` (sessionStorage), prompts
 * for the 6-digit code emailed by the API, then exchanges it for a
 * Sanctum bearer token via POST /v1/auth/magic-link/authenticate.
 *
 * Bouncing here without a stashed email (e.g. from a back-navigation
 * after the email cleared, or from a direct URL) sends the user
 * back to /login. We intentionally don't accept the email as a URL
 * query param — keeping it in sessionStorage avoids leaking the
 * address into browser history / referer headers / Cloudflare logs.
 */
export const CodeSentPage = () => {
  const navigate = useNavigate();

  // Resolve email once on mount. `peek` (not `consume`) so a refresh
  // here doesn't lose the address — the user might paste the code
  // 30s later after switching to their inbox tab.
  const [email] = useState<string | null>(() => peekMagicAuthEmail());
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resendNotice, setResendNotice] = useState<string | null>(null);

  const authenticate = useAuthenticateMagicAuth(async (token) => {
    await tokenStore.set(token);
  });
  const resend = useSendMagicAuthCode();

  // No email stashed → drop back to /login.
  useEffect(() => {
    if (!email) {
      void navigate({ to: '/login', replace: true });
    }
  }, [email, navigate]);

  const handleSubmit = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!email || code.length !== CODE_LENGTH || authenticate.isPending) return;

    setError(null);

    try {
      await authenticate.mutateAsync({
        email,
        code,
        device_name: deviceName,
      });

      // Sign-in succeeded; clear the stashed email and forward.
      consumeMagicAuthEmail();

      const returnUrl = consumePostLoginReturnUrl();
      if (returnUrl) {
        window.location.assign(returnUrl);
      } else {
        await navigate({ to: '/' });
      }
    } catch (e) {
      // Org-membership / beta-allowlist gate → polished /access-denied.
      if (isAccessGateBlocked(e)) {
        await navigate({ to: '/access-denied', replace: true });
        return;
      }

      setError(
        e instanceof Error
          ? e.message
          : 'That code didn’t work. Double-check the email and try again.',
      );
      setCode('');
    }
  };

  // Auto-submit the moment all 6 digits land. Cleaner than asking
  // the user to also press a button after typing the last digit.
  useEffect(() => {
    if (code.length === CODE_LENGTH && !authenticate.isPending) {
      void handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleResend = async () => {
    if (!email || resend.isPending) return;
    setError(null);
    setResendNotice(null);

    try {
      await resend.mutateAsync({ email });
      setResendNotice('A new code is on its way.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not resend the code.');
    }
  };

  return (
    <AuthShell
      width="sm"
      header={
        <AuthHeader
          title="Check your email"
          subtitle={
            email ? (
              <>
                We sent a 6-digit sign-in code to{' '}
                <span className="text-foreground font-medium">{email}</span>.
              </>
            ) : undefined
          }
        />
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit} noValidate>
        <div className="flex justify-center">
          <InputOTP
            value={code}
            onChange={setCode}
            maxLength={CODE_LENGTH}
            pattern={REGEXP_ONLY_DIGITS}
            inputMode="numeric"
            autoFocus
            disabled={authenticate.isPending}
            aria-label="Magic auth code"
          >
            <InputOTPGroup>
              {Array.from({ length: CODE_LENGTH }).map((_, i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>

        {error ? (
          <p className="text-destructive text-center text-[12.5px]" role="alert">
            {error}
          </p>
        ) : null}
        {resendNotice && !error ? (
          <p className="text-foreground-muted text-center text-[12.5px]" role="status">
            {resendNotice}
          </p>
        ) : null}

        <Button
          type="submit"
          disabled={code.length !== CODE_LENGTH || authenticate.isPending}
          className="w-full"
        >
          {authenticate.isPending ? 'Verifying…' : 'Verify code'}
        </Button>
      </form>

      <div className="border-border space-y-2.5 border-t pt-4">
        <Button
          type="button"
          variant="ghost"
          onClick={handleResend}
          disabled={resend.isPending || authenticate.isPending}
          className="text-foreground-muted h-8 w-full justify-center gap-1.5 text-[12.5px]"
        >
          <RefreshCw
            className={`size-3.5 ${resend.isPending ? 'animate-spin' : ''}`}
            aria-hidden="true"
          />
          {resend.isPending ? 'Sending…' : 'Resend code'}
        </Button>

        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            consumeMagicAuthEmail();
            void navigate({ to: '/login' });
          }}
          className="text-foreground-faint h-8 w-full justify-center gap-1.5 text-[12.5px]"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" />
          Use a different email
        </Button>
      </div>
    </AuthShell>
  );
};
